import { comparer, reaction, toJS } from 'mobx';
import {
  addDisposer, addMiddleware, Instance, types,
} from 'mobx-state-tree';
import { LngLatBounds } from 'mapbox-gl';
import moment from 'moment';
import getGeolocationByIP from '../../services/geolocation';
import getBoundsFromVisits from '../../helpers/visits/getBoundsFromVisits';
import Auth from './Auth';
import Account from './Account';
import Team from './Team';
import CustomerNotification from './CustomerNotification';
import UI from './UI';
import Unscheduled from './Unscheduled';
import Route from './Route';
import Router from './Router';
import Model from './Model';
import MapView from './MapView';
import Subscription from './Subscription';
import Upload from './Upload';
import Workspace from './Workspace';
import PlanningActions from './PlanningActions';
import Timeline from './Timeline';
import DriverLocation, { getRoutesLastLocation } from './DriverLocation';
import OrdersPage from './OrdersPage';
import CustomerProfiles from './CustomerProfiles';
import SchedulingWorkflow from './SchedulingWorkflow';
import Shopify from './Shopify';
import InternalToolsUI from './InternalToolsUI';
import Webhooks from './Webhooks';
import handleUnauthenticated from './middlewares/handleUnauthenticated';
import handleUndoRedoActions from './middlewares/handleUndoRedoActions';
import { logglyLogger } from '../../services/logglyLogger';
import {
  DriverDtoModelType,
  OrganizationOptimizationPreferencesDtoModelType,
  RouteCalendarSummaryDtoModelType,
  RouteDtoModelType,
  RoutesStatsDtoModelType,
  RouteTemplatesDtoModelType,
  VisitDtoModelType,
  LocationStatus,
  ProjectDtoModelType,
  DriverAssignInfoDtoModelType,
} from './models';
import getAppEnv from './utils/getAppEnv';
import sortByName from '../../helpers/sort/sortByName';
import { defaultDateFormat } from '../../constants';
import getAppRoot from './utils/getAppRoot';
import { isScheduledView, isUnscheduledView } from '../../components/scheduling/ViewMenu';
import { resolveTabOnDataChange } from '../../helpers/chooseTabForDate';
import { createRouteExistenceTracker } from '../../helpers/routeExistenceTracker';
import { Pages, SchedulingQueryParams } from './Router/constants';
import Organization from './Organization';
import getBoundsFromRoutes from '../../helpers/routes/getBoundsFromRoutes';
import getUnpublishedRoutesCount, { isPublishableRoute } from './utils/getUnpublishedRoutesCount';
import getFilteredAndSorted from './utils/getFilteredAndSorted';
import { filterOptimizationPreferencesForTracking } from './utils/filterOptimizationPreferencesForTracking';
import Billing from './Billing';
import Gleap from 'gleap';
import config from '../../config';
import handleFreeTierLimitExceeded from './middlewares/handleFreeTierLimitExceeded';
import handleFailedAction from './middlewares/handleFailedAction';
import handleRateLimited from './middlewares/handleRateLimited';
import { geocodingMessage } from '../../notifications/geocode';
import Insights from './Insights';
import Integration from './Integration';
import posthog from 'posthog-js/dist/module.full';
import sortByScoreAndName from '../../helpers/sort/sortByScoreAndName';
import DriverSubscription from './subscriptions/DriverSubscription';
import ProjectSubscription from './subscriptions/ProjectSubscription';
import RouteSubscription from './subscriptions/RouteSubscription';
import VisitSubscription from './subscriptions/VisitSubscription';
import PlanningActionsSubscription from './subscriptions/PlanningActionsSubscription';
import WorkspaceListingSubscription from './subscriptions/WorkspaceListingSubscription';
import UserSubscription from './subscriptions/UserSubscription';
import BillingSubscription from './subscriptions/BillingSubscription';
import UserInviteSubscription from './subscriptions/UserInviteSubscription';
import MyPendingInvitesSubscription from './subscriptions/MyPendingInvitesSubscription';
import { restoreActiveActions } from './subscriptions/handlers/planningActions/restoreActiveActions';
import resolveLastViewedDate from './utils/resolveLastViewedDate';
import SelectedVisitQuery from './queries/visits/SelectedVisitQuery';

const onStoreInit = (self: Models) => {
  // TODO: Include actionLogger if in dev/debug mode?
  // addDisposer(self, addMiddleware(self, actionLogger));
  addDisposer(self, addMiddleware(self.model, handleUnauthenticated));
  addDisposer(self, addMiddleware(self.model, handleUndoRedoActions));
  addDisposer(self, addMiddleware(self.model, handleFreeTierLimitExceeded));
  addDisposer(self, addMiddleware(self.model, handleFailedAction));
  addDisposer(self, addMiddleware(self.model, handleRateLimited));

  return {
    async afterCreate() {
      if (config.thirdParty.posthog.enabled && config.thirdParty.posthog.token) {
        const { proxyHost } = config.thirdParty.posthog;
        // Opaque proxy prefix (ROUT-2891): ad-blockers match PostHog by URL path
        // (e.g. /ingest) and by extension script filename, not just by domain, so
        // both the API prefix and the blockable filenames below need to be hidden.
        const PH_PROXY_PREFIX = '/rt-7fq2';
        const PH_ASSET_MAP: Record<string, string> = {
          'posthog-recorder.js': 'r-8k2p.js',
          'lazy-recorder.js': 'lr-3n7v.js',
          'dead-clicks-autocapture.js': 'dc-4m9q.js',
          'surveys.js': 'sv-6p2w.js',
          'exception-autocapture.js': 'ex-2q8k.js',
          'web-vitals.js': 'wv-5r3m.js',
          'web-vitals-with-attribution.js': 'wva-7t4c.js',
          'tracing-headers.js': 'th-9x4z.js',
        };
        const initPosthog = () => {
          posthog.init(`${config.thirdParty.posthog.token}`, {
            // Assets/ingestion route through our first-party proxy (Envoy /rt-7fq2)
            // when proxyHost is configured, so ad-blockers can't match *.i.posthog.com.
            api_host: proxyHost ? `https://${proxyHost}${PH_PROXY_PREFIX}` : 'https://us.i.posthog.com',
            // ui_host keeps toolbar/app links pointed at the real PostHog app even
            // though api_host is proxied.
            ui_host: 'https://us.posthog.com',
            segment: window.analytics, // Pass window.analytics here - NOTE: `window.` is important
            capture_pageview: false, // You want this false if you are going to use segment's `analytics.page()` for pageviews
            // When the posthog library has loaded, call `analytics.page()` explicitly.
            loaded: () => window.analytics?.page(),
            session_recording: {
              captureCanvas: {
                recordCanvas: true,
              },
            },
            // Rewrites blockable extension script filenames (e.g. posthog-recorder.js)
            // to opaque names under the proxy prefix before the <script> is added to
            // the DOM, since ad-blockers filter on those literal filenames as well.
            // Envoy maps the opaque names back to the real assets. Only relevant when
            // proxyHost is set - otherwise scripts load from *.i.posthog.com directly.
            prepare_external_dependency_script: proxyHost
              ? (script: HTMLScriptElement) => {
                try {
                  const url = new URL(script.src);
                  const match = Object.entries(PH_ASSET_MAP).find(
                    ([real]) => url.pathname.endsWith(`/static/${real}`),
                  );
                  if (match) {
                    const [, opaque] = match;
                    url.pathname = `${PH_PROXY_PREFIX}/${opaque}`;
                    script.src = url.toString();
                  }
                } catch {
                  // Leave script.src unchanged if it isn't a parseable absolute URL.
                }
                return script;
              }
              : undefined,
          });
        };
        // window.analytics is only defined when Segment is initialised (i.e. a Segment
        // token is set). Bridge PostHog through Segment when present; otherwise init
        // PostHog directly so it still works without a Segment token (e.g. local dev).
        if (window.analytics) {
          window.analytics.ready(initPosthog);
        } else {
          initPosthog();
        }
      }

      if (config.thirdParty.gleap.token) {
        Gleap.initialize(config.thirdParty.gleap.token);
      }
      if (!self.auth.isVerified && self.auth.token != null) {
        self.auth.verify();
      }

      getGeolocationByIP()
        .then((geolocation) => {
          self.ui.setGeolocation({
            countryCode: geolocation.country_code2.toLowerCase(),
            latitude: Number(geolocation.latitude),
            longitude: Number(geolocation.longitude),
          });
        })
        .catch(() => {
          // eslint-disable-next-line no-console
          console.error('Failed to get geolocation by IP address.');
        });
    },
  };
};

// on user, start or stop the billing subscription and my pending invites subscription
const onOrganization = (self: Models) => {
  addDisposer(self, reaction(() => self.account?.user?.organization, (organization) => {
    if (organization) {
      getAppRoot(self).subscription.startBillingSubscription();
      getAppRoot(self).subscription.startMyPendingInvitesSubscription();
    } else {
      getAppRoot(self).subscription.stopBillingSubscription();
      getAppRoot(self).subscription.stopMyPendingInvitesSubscription();
    }
  }));

  return {};
};

const fetchRoutesStats = (self: Models) => {
  if (self.ui.selectedCalendarView && self.ui.calendarRange) {
    const [rangeStart, rangeEnd] = self.ui.calendarRange;
    self.model.getRoutesStats(rangeStart, rangeEnd);
  } else {
    self.model.getRoutesStats();
  }
};

const onRoutesChange = (self: Models) => {
  // Fetch fleet-level aggregate stats when stats-relevant route fields change.
  // Gates on isBackgroundRoutesFetching to avoid firing during paginated route loading.
  // Uses a string fingerprint instead of toJS() for structural equality comparison.
  addDisposer(self, reaction(() => {
    if (self.model.isBackgroundRoutesFetching) return null;

    const routes = Array.from<RouteDtoModelType>(self.model.routeDtos.values());
    return `${routes.length}:${routes.map((route) => {
      const s = route.resolvedActiveRouteSolution;
      const c = route.constraints;
      return [s?.ordersCount, s?.stopsCount, s?.distance, s?.workingTime, s?.load, c?.capacity].join(',');
    }).join('|')}`;
  }, (current) => {
    if (current === null) return;
    if (!self.ui.selectedWorkspace || !self.ui.selectedDate) return;

    fetchRoutesStats(self);
  }, { delay: 1000 }));

  return {};
};

const onRouteDriverLocation = (self: Models) => {
  addDisposer(self, reaction(() => ({
    isWorkspaceRouteLoading: self.model.isWorkspaceRouteLoading,
    selectedWorkspaceId: self.ui.selectedWorkspace?.id,
    userId: self.account.user?.id,
    token: self.auth.token,
    isOptimizing: self.ui.isOptimizing,
  }), async ({
    isWorkspaceRouteLoading,
    selectedWorkspaceId,
    userId,
    token,
    isOptimizing,
  }) => {
    const setup = userId != null && token != null && !isWorkspaceRouteLoading && !isOptimizing;
    const startDriverLocationTracking = setup && selectedWorkspaceId != null;

    try {
      if (setup) {
        self.driverLocation.login(`v3-${userId}`);
      } else {
        self.driverLocation.logout();
      }

      if (startDriverLocationTracking) {
        await self.driverLocation.start(Number(selectedWorkspaceId));
      }
    } catch (error) {
      console.error('Error managing driver location tracking', error);
    }
  }));

  return {};
};

const onselectWorkspace = (self: Models) => {
  addDisposer(self, reaction(() => self.ui.selectedWorkspace?.id, async (id) => {
    if (id == null) {
      self.mapView.reset();
      self.timeline.reset();
      self.ui.setShowImportDialog(false);
      self.ui.setShowUploadDropzone(false);
    } else {
      try {
        await Promise.all([
          self.model.mutateSetDefaultWorkspaceId({ workspaceId: Number(id) }).currentPromise(),
          self.model.getOptimizationPreferences(),
        ]);
      } catch {
        logglyLogger.log({ error: 'Workspace setup failed', info: `project ${id}` });
      } finally {
        self.planningActions.setIsLoading(false);
      }
    }
  }, { delay: 100 }));

  addDisposer(self, reaction(() => self.ui.selectedWorkspace, async () => {
    if (self.ui.selectedWorkspace) {
      await self.organization.getOrganizationMetadata();
      await self.shopify.fetchInstallation();

      try {
        await self.billing.getBillingDetails();
      } catch (err) {
        logglyLogger.log({ error: 'No Billing Details', info: `Failed to get billing details for project ${self.account?.user?.organization?.name}` });
      }

      try {
        await self.customerNotification.getCNSettings();
      } catch {
        logglyLogger.log({ error: 'No CN Settings', info: `Failed to load CN for organization ${self.account?.user?.organization?.name}` });
      }
    }
  }));

  addDisposer(self, reaction(() => self.ui.selectedWorkspace, async (project) => {
    if (project?.id != null && !project?.deleted && self.router.location.pathname.includes('/orders')) {

      const selectedOrder = self.router.queryParams.get(SchedulingQueryParams.ORDER);

      if (selectedOrder) {
        try {
          getAppRoot(self).ui.setIsOrdersLoadingOnInit(true);
          await self.model.queryOrderByUuid({ projectId: project.id, uuid: selectedOrder }, SelectedVisitQuery);
          getAppRoot(self).ui.setIsOrdersLoadingOnInit(false);
        } catch (error) {
          getAppRoot(self).ui.setIsOrdersLoadingOnInit(false);
          logglyLogger.log({ error: 'Could not query order by UUID', info: `${error}` });
        }
      }
    }
  }));

  addDisposer(self, reaction(() => self.ui.selectedWorkspace, async (project) => {
    if (self.router.location.pathname.includes('/drivers') || self.router.location.pathname.includes('/settings')) {
      return;
    }

    if (project?.id != null && !project?.deleted) {
      self.ui.setIsOptimizing(false);
      getAppEnv(self).closeAllMessages();

      await self.model.getRouteTemplatesOverview(project.id);

      // Guard: Check if workspace is still selected
      if (self.ui.selectedWorkspace?.id !== project.id) {
        return; // Workspace changed, abort to avoid inconsistent state
      }

      const activePlanningActions = await self.planningActions.getActivePlanningActions();

      if (activePlanningActions && activePlanningActions.length > 0) {
        await restoreActiveActions(activePlanningActions, self);
      }
    }
  }));

  return {};
};

const onVisitGeocoding = (self: Models) => {
  addDisposer(self, reaction(() => self.ui.selectedWorkspace?.visitGeocodingCount, (count) => {
    if (count && count > 0) {
      getAppEnv(self).message(geocodingMessage(count));
    }
  }));

  return {};
};

const scheduledVisits = (visits: VisitDtoModelType[]) => (visits
  .filter((v) => v.isScheduled)
);

const onUnpublishedRoutesBadge = (self: Models) => {
  // Backs the nav's unpublished-routes badge on pages the full route fetch in
  // onSelectDate doesn't cover: dateless workspace pages (orders, drivers,
  // customers) and non-workspace pages, where a direct landing (e.g. right
  // after login) would otherwise leave routeDtos empty and the badge hidden.
  // Fetches only the publish-status fields for the day the routes page would
  // restore. On the routes page (?date= present) the full fetch owns loading,
  // so this stays quiet there.
  addDisposer(self, reaction(
    () => ({ workspaceId: self.ui.displayedWorkspace?.id, selectedDate: self.ui.selectedDate }),
    ({ workspaceId, selectedDate }) => {
      if (selectedDate || !workspaceId) return;
      const date = resolveLastViewedDate(self.ui.displayedWorkspace);
      self.model.fetchRoutesPublishStatus(String(workspaceId), date);
    },
    { delay: 100 },
  ));
  return {};
};

const onSelectDate = (self: Models) => {
  addDisposer(self, reaction(() => toJS({ selectedWorkspace: self.ui.selectedWorkspace, selectedDate: self.ui.selectedDate }), ({ selectedDate }) => {
    if (selectedDate == null) {
      if (self.router.location.pathname.includes('/routes')) {
        localStorage.removeItem('selectedWorkspaceDate');
      }
    } else {
      const selectedDateMoment = moment.utc(selectedDate);
      if (selectedDateMoment.isValid()) {
        localStorage.setItem('selectedWorkspaceDate', selectedDateMoment.format(defaultDateFormat));
      }
    }
  }, { delay: 300 }));

  addDisposer(self, reaction(() => self.ui.selectedDate, async (selectedDate) => {
    if (selectedDate) {
      getAppRoot(self).model.deleteScheduledVisits();
      getAppRoot(self).model.resetModelForDate();
      getAppRoot(self).ui.clearSelectedRoutesBulk();
      getAppRoot(self).model.setUnscheduledVisitsLatLngsLoading(true);
      // The previous day's settled order doesn't apply to the new day; fetchProjectRoutes re-settles.
      getAppRoot(self).timeline.clearSettledRouteOrder();
    }
  }, { delay: 0 }));

  addDisposer(self, reaction(() => toJS({ selectedWorkspace: self.ui.selectedWorkspace, selectedDate: self.ui.selectedDate }), ({ selectedWorkspace, selectedDate }) => {
    if (selectedDate && selectedWorkspace) {
      const projectId = selectedWorkspace?.id;

      // Skip route fetching on non-scheduling pages. Dateless pages are
      // covered by the lightweight badge fetch in onUnpublishedRoutesBadge.
      const { pathname } = self.router.location;
      if (pathname.includes('/drivers') || pathname.includes('/settings')) return;

      getAppRoot(self).timeline.setLoading(true);
      getAppRoot(self).ui.setIsRoutesLoadingOnInit(true);
      getAppRoot(self).ui.setIsOrdersLoadingOnInit(true);
      self.model.fetchUnscheduledVisitsLatLngs(projectId)
        .then(() => {
          getAppRoot(self).ui.setIsOrdersLoadingOnInit(false);
        })
        .catch(() => {
          getAppRoot(self).ui.setIsOrdersLoadingOnInit(false);
          logglyLogger.log({ error: 'fetchUnscheduledVisitsLatLngs failed', info: `project ${projectId}` });
        });

      fetchRoutesStats(self);

      self.model.fetchProjectRoutes(projectId, selectedDate)
        .then(() => {
          getAppRoot(self).ui.setIsRoutesLoadingOnInit(false);
        })
        .catch(() => {
          getAppRoot(self).ui.setIsRoutesLoadingOnInit(false);
          logglyLogger.log({ error: 'fetchProjectRoutes failed', info: `project ${projectId}` });
        });
    }
  }, { delay: 100 }));

  addDisposer(self, reaction(() => toJS({
    selectedWorkspace: self.ui.selectedWorkspace,
    selectedDate: self.ui.selectedDate,
    isWorkspaceRouteLoading: self.model.isWorkspaceRouteLoading,
  }), ({ selectedWorkspace, selectedDate, isWorkspaceRouteLoading }) => {
    if (selectedDate && selectedWorkspace && !isWorkspaceRouteLoading) {
      const projectId = selectedWorkspace?.id;
      self.model.fetchProjectRoutesExtraData(projectId, selectedDate)
        .then((publishedRouteUuids: string[]) => {
          self.ui.fetchOrderDeliveryProgress(parseInt(projectId, 10), publishedRouteUuids);
        })
        .catch(() => {
          logglyLogger.log({ error: 'No Selected date', info: `Failed to get routes for project ${projectId}` });
        });
    }
  }, { delay: 100 }));

  // Persist the last sub-view per tab so the tab-matches-date rule (and the
  // view menu) can restore it. A null view is deliberately NOT a reset: plain
  // date toggles clear the view param so the rule re-decides for the new date
  // (see buildDateToggleParams), and wiping the stored sub-views on that null
  // was what snapped users back to Timeline when moving across dates
  // (ROUT-2644). Preferences only change when the user lands on a sub-view.
  addDisposer(self, reaction(() => self.ui.currentView, async (view) => {
    if (view == null) return;
    if (isScheduledView(view)) {
      localStorage.setItem('selectedScheduledView', view);
    }
    if (isUnscheduledView(view)) {
      localStorage.setItem('selectedUnscheduledView', view);
    }
  }, { delay: 200 }));

  // Tab-matches-date: keep the active tab aligned with whether the selected
  // date has routes. Empty date → Unscheduled (CSV dropzone is the useful
  // empty state). Date with routes → Scheduled (timeline is where the
  // action is). Deps are limited to data signals — selectedDate, workspace,
  // loading flag, and whether the date has any routes — so user-driven tab
  // clicks never re-trigger this. We track `hasRoutes` as a boolean rather
  // than the route count so we only fire on the 0↔non-zero transition that
  // actually matters for the tab rule. The decision itself lives in
  // chooseTabForDate so the matrix is unit-testable.
  //
  // An explicit view in the URL wins (manual click, deep link, or the
  // "create orders from customers" toast that wants the Not Scheduled tab),
  // so we only override it when the selected date's route-existence flips
  // mid-session. The tracker reports that flip only when it happens within the
  // same workspace+date context the user is sitting in; we feed it the last
  // *settled* (non-loading) state, so a cross-date navigation (which toggles the
  // loading flag) and a workspace switch (which changes the context key and
  // clears the view + date params in selectWorkspace) can never be mistaken for
  // a same-context 0↔1 change.
  const routeExistenceTracker = createRouteExistenceTracker();
  addDisposer(self, reaction(() => toJS({
    selectedDate: self.ui.selectedDate,
    workspaceId: self.ui.selectedWorkspace?.id,
    isSynced: !!self.ui.selectedWorkspace?.isSynced,
    isWorkspaceRouteLoading: self.model.isWorkspaceRouteLoading,
    hasRoutes: self.sortedRoutes.length > 0,
    // Track scheduling-page entry as a signal: arriving on the routes page must
    // fire the reaction even when no data signal happens to change, or a missing
    // ?view= would never get resolved — the page would render with no tab/panel.
    // The off-page guard in resolveTabOnDataChange keeps this from disturbing
    // the route-existence tracker when we leave the page.
    onSchedulingPage: self.router.location.pathname.includes(`/${Pages.SCHEDULING}`),
    // Organizations without the unscheduled tray must land on Routes even on an
    // empty date. Tracked as a signal so a variant that resolves after boot (the
    // `me` payload arrives async) re-decides rather than leaving them on a tray.
    allowUnscheduledViews: self.ui.canViewUnscheduledViews,
  }), (signals) => {
    const target = resolveTabOnDataChange(routeExistenceTracker, {
      ...signals,
      currentView: self.ui.currentView,
    });
    if (target !== null) {
      self.ui.setCurrentView(target);
    }
  }, { delay: 100 }));

  addDisposer(self, reaction(() => self.ui.selectedCalendarView, async (view) => {
    if (view) {
      localStorage.setItem('selectedCalendarView', view);
    }
  }, { delay: 200 }));

  return {};
};

const onUnscheduledOrdersCount = (self: Models) => {
  // Orders nav badge: fetch the workspace-wide unscheduled total whenever the stored count
  // isn't for the displayed workspace (first load, workspace switch, or a dropped late reply).
  // Returning to the same workspace (e.g. from /settings) leaves the tag matching, so the
  // number stays on screen. A failed fetch leaves the tag stale without re-firing, so no loop.
  addDisposer(self, reaction(() => ({
    workspaceId: self.ui.displayedWorkspace?.id,
    isStale: self.model.unscheduledOrdersCountWorkspaceId !== self.ui.displayedWorkspace?.id,
  }), ({ workspaceId, isStale }) => {
    if (workspaceId != null && isStale) {
      self.model.fetchUnscheduledOrdersTotalCount(workspaceId);
    }
  }, { fireImmediately: true, equals: comparer.structural }));

  // Refetch when the locally-known unscheduled set changes (uploads, deletes,
  // (un)scheduling, subscription merges).
  addDisposer(self, reaction(() => {
    const visits = self.model.scopedVisits;
    return {
      workspaceId: self.ui.selectedWorkspace?.id,
      isLoading: self.model.isWorkspaceLoading,
      counts: `${visits.length}:${visits.filter((v) => !v.isScheduled).length}`,
    };
  }, ({ workspaceId, isLoading, counts }, prev) => {
    // Skip when the workspace itself changed (the displayedWorkspace reaction
    // already fetched) and while workspace data is loading.
    if (workspaceId == null || isLoading || workspaceId !== prev?.workspaceId) {
      return;
    }
    // Loading finishing flips isLoading with counts usually already settled
    // (prev advances during the suppressed evaluations) — that state is covered
    // by the switch-time fetch, so only a counts delta warrants a refetch.
    if (counts === prev.counts) {
      return;
    }
    self.model.fetchUnscheduledOrdersTotalCount(workspaceId);
  }, { delay: 1000, equals: comparer.structural }));

  return {};
};

const onSubscription = (self: Models) => {
  // Start or stop project-related subscriptions when project changes
  addDisposer(self, reaction(() => self.ui.selectedWorkspace?.id, (projectId) => {
    if (projectId != null) {
      self.subscription.startProjectSubscription(projectId);
      self.subscription.startRouteSubscription(projectId);
      self.subscription.startVisitSubscription(projectId);
      self.subscription.startDriverSubscription(projectId);
      self.subscription.startPlanningActionsSubscription(projectId);
    } else {
      self.subscription.stopProjectSubscriptions();
    }
  }, { fireImmediately: true }));

  return {};
};

const views = (self: Models) => ({
  get visits(): VisitDtoModelType[] {
    // Workspace-scoped at the Model layer; single source every order view derives from.
    return self.model.scopedVisits;
  },
  get drivers(): DriverDtoModelType[] {
    return Array.from(self.model.driverDtos.values());
  },
  get sortedDrivers(): DriverDtoModelType[] {
    return getFilteredAndSorted(this.drivers, (driver) => !driver.deleted, (driverA, driverB) => sortByName(driverA.name, driverB.name));
  },
  sortedDriversForAssignmentToRoute(drivers: DriverAssignInfoDtoModelType[]): DriverAssignInfoDtoModelType[] {
    return getFilteredAndSorted(drivers, (driver) => !driver.deleted, (driverA, driverB) => sortByScoreAndName(driverA, driverB));
  },
  get workspaces(): ProjectDtoModelType[] {
    return Array.from(self.model.projectDtos.values());
  },
  get sortedWorkspaces(): ProjectDtoModelType[] {
    return getFilteredAndSorted(this.workspaces, (p: ProjectDtoModelType) => !p.deleted, (a: ProjectDtoModelType, b: ProjectDtoModelType) => Number(b.id) - Number(a.id));
  },
  get workspaceOptimizationPreferences(): OrganizationOptimizationPreferencesDtoModelType | undefined {
    return Array.from<OrganizationOptimizationPreferencesDtoModelType>(self.model.organizationOptimizationPreferencesDtos.values()).find(
      (preference) => preference.projectId === Number(self.ui.selectedWorkspace?.id));
  },
  get workspaceOptimizationPreferencesForTracking(): Record<string, any> | undefined {
    return filterOptimizationPreferencesForTracking(this.workspaceOptimizationPreferences);
  },
  get fallbackDefaultOrderDuration(): number {
    return 10;
  },
  get defaultOrderDuration(): number {
    if (typeof this.workspaceOptimizationPreferences?.defaultOrderDuration === 'number') {
      return this.workspaceOptimizationPreferences?.defaultOrderDuration / 60;
    }
    return this.fallbackDefaultOrderDuration;
  },
  routesStatsByDate(date?: string, projectId?: string): RoutesStatsDtoModelType | undefined {
    const currentProjectId = projectId || self.ui.selectedWorkspace?.id;
    const currentDate = date || self.ui.selectedDate;

    return self.model.routesStatsDtos.get(`${currentProjectId}-${currentDate}`);
  },
  get routeTemplates(): RouteTemplatesDtoModelType[] {
    return Array
      .from(self.model.routeTemplatesDtos.values() as unknown as RouteTemplatesDtoModelType[])
      .sort((templateA, templateB) => {
        // We don't actually expect createdOn to be undefined
        const dateA = +new Date(templateA.createdOn!);
        const dateB = +new Date(templateB.createdOn!);
        // Unary converts the operand to number for sort
        return dateA - dateB;
      });
  },
  get activeRouteTemplates(): RouteTemplatesDtoModelType[] {
    return this.routeTemplates.filter((template: RouteTemplatesDtoModelType) => template.active);
  },
  get inactiveRouteTemplates(): RouteTemplatesDtoModelType[] {
    return this.routeTemplates.filter((template: RouteTemplatesDtoModelType) => !template.active);
  },
  sortedRoutesByDay(date?: string): RouteDtoModelType[] {
    const dateToUse = date || self.ui.selectedDate;
    const routes = self.model.routesRange.get(`${dateToUse}`) || [];
    return [...routes]
      .filter((route) => route.name !== undefined)
      .sort((routeA, routeB) => sortByName(routeA.name, routeB.name));
  },
  sortedCalendarRoutesByDay(date?: string): RouteCalendarSummaryDtoModelType[] {
    const dateToUse = date || self.ui.selectedDate;
    const routes = self.model.calendarRoutesRange.get(`${dateToUse}`) || [];
    return [...routes].sort((routeA, routeB) => sortByName(routeA.name, routeB.name));
  },
  get sortedRoutes(): RouteDtoModelType[] {
    const { selectedDate } = self.ui;

    return this.sortedRoutesByDay(selectedDate);
  },
  get areAllRoutesPublished(): boolean {
    const publishedCount: number = this.sortedRoutes.reduce((sum, cur) => sum + (cur.isDispatched ? 1 : 0), 0);
    return publishedCount === this.sortedRoutes.length;
  },
  get routesLastLocations() {
    return getRoutesLastLocation(this.sortedRoutes);
  },
  get orders(): VisitDtoModelType[] {
    // Delegate to `visits` so workspace scoping lives in one place.
    return this.visits;
  },
  get ordersCount(): number {
    return this.orders.length;
  },
  get ordersInvalidAddressCount(): number {
    return this.orders.filter(v => v.locationStatus === LocationStatus.Invalid).length;
  },
  get ordersQuestionableAddressCount(): number {
    return this.orders.filter(v => v.activeLocation && !v.activeLocation.bestMatch && v.locationStatus === LocationStatus.Validated).length;
  },
  get scheduledVisits(): VisitDtoModelType[] {
    return scheduledVisits(this.orders);
  },
  get unscheduledVisits(): VisitDtoModelType[] {
    return this.orders.filter(v => !v.isScheduled && v.isFilteredDate);
  },
  get unscheduledVisitsCount(): number {
    return this.unscheduledVisits.length;
  },
  get unscheduledVisitsInvalidAddressCount(): number {
    return this.unscheduledVisits.filter(v => v.locationStatus === LocationStatus.Invalid).length;
  },
  get unscheduledVisitsQuestionableAddressCount(): number {
    return this.unscheduledVisits.filter(v => v.activeLocation && !v.activeLocation.bestMatch && v.locationStatus === LocationStatus.Validated).length;
  },
  get scheduledVisitsQuestionableAddressCount(): number {
    return this.scheduledVisits.filter(v => !v.activeLocation.bestMatch).length;
  },
  get unscheduledVisitsBounds(): LngLatBounds | null {
    return getBoundsFromVisits(this.unscheduledVisits);
  },
  get routesBounds(): LngLatBounds | null {
    return getBoundsFromRoutes(this.sortedRoutes);
  },
  get allOrdersBounds(): LngLatBounds | null {
    const { unscheduledVisitsBounds, routesBounds } = this;
    if (routesBounds && unscheduledVisitsBounds) {
      return routesBounds.extend(unscheduledVisitsBounds);
    }
    if (routesBounds) {
      return routesBounds;
    }
    if (unscheduledVisitsBounds) {
      return unscheduledVisitsBounds;
    }
    return null;
  },
  get isEmpty(): boolean {
    return !this.visits?.length && !this.sortedRoutes?.length;
  },
  // Day-scoped so callers that schedule into a day other than `?date=` (the
  // scheduling preview's own date stepper, and the orders page — which carries no
  // date param at all) can ask about the day they actually target (ROUT-3624).
  isOptimizedByDay(date?: string): boolean {
    return this.sortedRoutesByDay(date).some((route) => (route.resolvedActiveRouteSolution?.stopsCount || 0) > 0);
  },
  get isOptimized(): boolean {
    return this.isOptimizedByDay(self.ui.selectedDate);
  },
  get publishableRoutes(): RouteDtoModelType[] {
    return this.sortedRoutes.filter(isPublishableRoute);
  },
  get unpublishedRoutesCount(): number {
    // Unlike `publishableRoutes` (strictly the URL's ?date=), this resolves
    // the date itself — URL date when present, else the persisted last-viewed
    // day — so the nav badge still counts on dateless/non-workspace pages.
    return getUnpublishedRoutesCount((date) => this.sortedRoutesByDay(date), self.ui.selectedDate, self.ui.displayedWorkspace);
  },
});

const App = types.model('App')
  .props({
    model: types.optional(Model, {}),
    auth: types.optional(Auth, {}),
    billing: types.optional(Billing, {}),
    organization: types.optional(Organization, {}),
    insights: types.optional(Insights, {}),
    ordersPage: types.optional(OrdersPage, {}),
    customerProfiles: types.optional(CustomerProfiles, {}),
    account: types.optional(Account, {}),
    team: types.optional(Team, {}),
    integration: types.optional(Integration, {}),
    shopify: types.optional(Shopify, {}),
    internalToolsUI: types.optional(InternalToolsUI, {}),
    webhooks: types.optional(Webhooks, {}),
    customerNotification: types.optional(CustomerNotification, {}),
    ui: types.optional(UI, {}),
    unscheduled: types.optional(Unscheduled, {}),
    route: types.optional(Route, {}),
    router: types.optional(Router, {}),
    subscription: types.optional(Subscription, {
      projectSubscriptionOptions: ProjectSubscription(),
      workspaceListingSubscriptionOptions: WorkspaceListingSubscription(),
      routeSubscriptionOptions: RouteSubscription(),
      visitSubscriptionOptions: VisitSubscription(),
      driverSubscriptionOptions: DriverSubscription(),
      planningActionsSubscriptionOptions: PlanningActionsSubscription(),
      userSubscriptionOptions: UserSubscription(),
      billingSubscriptionOptions: BillingSubscription(),
      userInviteSubscriptionOptions: UserInviteSubscription(),
      myPendingInvitesSubscriptionOptions: MyPendingInvitesSubscription(),
    }),
    upload: types.optional(Upload, {}),
    mapView: types.optional(MapView, {}),
    workspace: types.optional(Workspace, {}),
    planningActions: types.optional(PlanningActions, {}),
    timeline: types.optional(Timeline, {}),
    driverLocation: types.optional(DriverLocation, {}),
    schedulingWorkflow: types.optional(SchedulingWorkflow, {}),
  })
  .views(views)
  .actions(onOrganization)
  .views(onRouteDriverLocation)
  .actions(onStoreInit)
  .actions(onRoutesChange)
  .actions(onselectWorkspace)
  .actions(onSelectDate)
  .actions(onUnscheduledOrdersCount)
  .actions(onUnpublishedRoutesBadge)
  .actions(onSubscription)
  .actions(onVisitGeocoding);

export interface Models {
  auth: Instance<typeof Auth>;
  account: Instance<typeof Account>;
  billing: Instance<typeof Billing>;
  customerNotification: Instance<typeof CustomerNotification>;
  customerProfiles: Instance<typeof CustomerProfiles>;
  mapView: Instance<typeof MapView>;
  model: Instance<typeof Model>;
  organization: Instance<typeof Organization>;
  insights: Instance<typeof Insights>;
  ordersPage: Instance<typeof OrdersPage>;
  team: Instance<typeof Team>;
  integration: Instance<typeof Integration>;
  shopify: Instance<typeof Shopify>;
  internalToolsUI: Instance<typeof InternalToolsUI>;
  webhooks: Instance<typeof Webhooks>;
  planningActions: Instance<typeof PlanningActions>;
  workspace: Instance<typeof Workspace>;
  route: Instance<typeof Route>;
  router: Instance<typeof Router>;
  ui: Instance<typeof UI>;
  subscription: Instance<typeof Subscription>;
  timeline: Instance<typeof Timeline>;
  unscheduled: Instance<typeof Unscheduled>;
  upload: Instance<typeof Upload>;
  driverLocation: Instance<typeof DriverLocation>;
  schedulingWorkflow: Instance<typeof SchedulingWorkflow>;
}

type ViewActions =
  & ReturnType<typeof views>
  & ReturnType<typeof onStoreInit>
  & ReturnType<typeof onselectWorkspace>
  & ReturnType<typeof onSubscription>
  & ReturnType<typeof onRouteDriverLocation>;

export interface IApp extends Models, ViewActions {
}

export default App;
